import { Alert } from "@mui/lab";
import { AppBar, FormHelperText, Tab, TextField, Toolbar } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { SquarePicture } from "assets/appConfig";
import { AppImages } from "assets/appResources";
import { FormStyleClasses, GlobalClassKeys, GrayBarStyle, TabLargeStyle } from "assets/jss/common-styles";
import { AppBarTabs } from "components/AppTabs";
import ButtonBack from "components/Buttons/ButtonBack";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { ImageSelection } from "components/FileUpload/ImageSelection";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { RecordAbout } from "components/RecordAbout";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldCounter from "components/TextFieldCounter";
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import { GroupType, SystemRoles } from "models/ApiEnums";
import { GetGroupDto, GroupItemChangedDto, SaveGroupDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { Prompt } from "react-router";
import GroupEditMember from "views/Group/Components/GroupEditMember";
import GroupEditRole from "views/Group/Components/GroupEditRole";
import GroupEditTopic from "views/Group/Components/GroupEditTopic";
import GroupLogDialog from "./GroupLogDialog";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    groupId?: string;
    onClose: (reload: boolean) => void;
}

interface IState {
    dialog: any,
    lastError: any,
    currentTab: TabOptions,
    formCycle: RequestLifeCycle,
    record: Partial<GetGroupDto>,
    validate: {
        name: boolean,
        nameIsUnique: boolean,
        thumbnail: boolean;
    };
}

type Props = IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

enum TabOptions { Members, ApplyToMembers }

/////////////////////////////////////////////////////////////////////////////////////

class GroupEdit extends React.Component<Props, IState> {

    private compareDto: any = null;

    ///
    constructor(props: Props) {
        super(props);

        this.compareDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            currentTab: TabOptions.Members,
            dialog: null,
            record: {
                groupId: null,
                name: "",
                description: "",
                thumbnail: null,
                ownRoles: SystemRoles.None,
                groupsRoles: SystemRoles.None,
                changedMembers: [],
                changedSuggestedTopics: [],
                recordInfo: null,
                counters: null,
                groupType: GroupType.UserDefined
            },
            validate: {
                name: null,
                nameIsUnique: null,
                thumbnail: true
            },
        };

    }


    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Group.Components.GroupEdit." + r, f);
    }

    ///
    private prepareComponent() {
        if (this.props.groupId != null && this.props.groupId != "create")
            this.continueLoad(this.props.groupId);
        else {
            this.compareDto = LibUtils.deepCopy(this.state.record);
            this.setState({ formCycle: RequestLifeCycle.InitSuccess });
        }
    }

    ///
    private continueLoad(groupId: string) {
        api.getGroupRecord(groupId)
            .then((dto) => {
                this.compareDto = LibUtils.deepCopy(dto);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record: dto,
                    validate: {
                        name: dto.name != null && dto.name.length > 0,
                        nameIsUnique: true,
                        thumbnail: true
                    }
                });
            })
            .catch((err: any) => {
                this.setState({
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private getPostDto(): SaveGroupDto {
        const { record } = this.state;

        return {
            groupId: record.groupId,
            name: record.name,
            description: record.description,
            thumbnail: record.thumbnail == null ? null : { fileId: record.thumbnail.fileId },
            changedMembers: record.changedMembers,
            changedSuggestedTopics: record.changedSuggestedTopics,
            ownRoles: record.ownRoles,
        };
    }

    ///
    private handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
        this.updateRecord(event.target.name, event.target.value);
    }

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {
            switch (name) {
                case "name":
                    state.record.name = v;
                    state.validate.name = v != "";
                    break;

                case "description":
                    state.record.description = v;
                    break;

                case "thumbnail":
                    if (v == null) state.record.thumbnail = null;
                    else state.record.thumbnail = { fileId: v.fileId, url: v.tempUrl };
                    break;

                case "thumbnailUploading":
                    state.validate.thumbnail = !v;
                    break;

                case "members":
                    state.record.changedMembers = v;
                    break;

                case "topics":
                    state.record.changedSuggestedTopics = v;
                    return state;


                case "ownRoles":
                    state.record.ownRoles = v;
                    break;
            }
            return state;
        });
    }


    ///
    private formIsValid() {
        return LibUtils.isAllPropertiesWithValue(this.state.validate, true);
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (!this.formIsValid()) return;

        this.setState({ formCycle: RequestLifeCycle.Posting });

        const dto = this.getPostDto();

        api.saveGroupRecord(dto)
            .then((response) => {
                this.props.snackbar.showMessage(this.res("UpdateSuccess"), "success");
                this.props.onClose(true);
            })
            .catch((err: any) => {
                this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        this.setState({ currentTab: value });
    };

    ///
    private isRecordChanged() {
        return this.state.lastError == null && !LibUtils.deepCompare(this.state.record, this.compareDto);
    }

    ///
    private checkName = () => {
        api.checkGroupNameIsUnique(this.state.record.groupId, this.state.record.name)
            .then((result) => {
                this.setState(state => {
                    state.validate.nameIsUnique = result;
                    return state;
                });
            });
    };

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}>
                <Toolbar>

                    <div className={GlobalClassKeys.grow}>
                        <ButtonBack
                            text={this.res("BackToList")}
                            onClick={() => {
                                if (this.isRecordChanged()) {
                                    if (window.confirm(this.props.t("Common.Question.Unsave"))) {
                                        this.props.onClose(false);
                                    }
                                }
                                else {
                                    this.props.onClose(false);
                                }
                            }}
                        />
                    </div>

                    <ButtonSave
                        disabled={!this.formIsValid()}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                    />
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <RecordAbout
                        record={this.state.record.recordInfo}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <GroupLogDialog
                                            groupId={this.state.record.groupId}
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderFormMain() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">
                <form noValidate autoComplete="off">

                    <MuiPaper>

                        {
                            record.groupType != GroupType.UserDefined &&
                            <Alert severity="warning">{this.res("SystemgGroupWarn")}</Alert>
                        }

                        <Grid container spacing={2}>
                            <Grid item>
                                <ImageSelection
                                    acceptedFiles={MediaUtils.AcceptImages()}
                                    allowEditSelectedImage={true}
                                    currentImageUrl={record && record.thumbnail ? record.thumbnail.url : null}
                                    dimensions={SquarePicture}
                                    emptyImageUrl={AppImages.DefaultGroupThumbnail}
                                    enableClear={true}
                                    onClear={() => this.updateRecord("thumbnail", null)}
                                    onFileUploadStarted={() => this.updateRecord("thumbnailUploading", true)}
                                    onFileUploadFinished={() => this.updateRecord("thumbnailUploading", false)}
                                    onFileUploadSuccess={(data) => this.updateRecord("thumbnail", data.thumbnail ? data.thumbnail : data.mainFile)}
                                />
                            </Grid>

                            <Grid item xs>

                                <Grid container spacing={1}>

                                    <Grid item xs={12}>
                                        <TextField
                                            autoFocus={record.groupType == GroupType.UserDefined}
                                            fullWidth
                                            required
                                            name="name"
                                            label={this.props.t<string>("Common.Literals.NameLabel")}
                                            autoComplete="off"
                                            error={validate.name === false}
                                            margin="dense"
                                            value={record.name || ""}
                                            onChange={(e) => this.handleChange(e)}
                                            onBlur={this.checkName}
                                            inputProps={
                                                {
                                                    maxLength: 255,
                                                }
                                            }
                                        />
                                        {validate.nameIsUnique === false && <FormHelperText error>{this.res("NameAlreadyExistsMessage")}</FormHelperText>}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextFieldCounter
                                            autoFocus={record.groupType != GroupType.UserDefined}
                                            fullWidth
                                            multiline
                                            label={this.props.t<string>("Common.Literals.DescriptionLabel")}
                                            placeholder={this.res("DescriptionPlaceholder")}
                                            value={record.description}
                                            maxLength={1500}
                                            rows={4}
                                            onChange={
                                                (v) => { this.updateRecord("description", v); }
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                    </MuiPaper>

                </form>
            </MuiContainer>
        );
    }

    ///
    private renderFormMenuBar(): JSX.Element {
        return (
            <MuiContainer margin="dense">
                <AppBar position="static" color="secondary">
                    <AppBarTabs centered value={this.state.currentTab} onChange={this.handleTabSelection}>
                        <Tab value={TabOptions.Members} label={this.res("TabMembers")} style={TabLargeStyle} />
                        <Tab value={TabOptions.ApplyToMembers} label={this.res("TabApplyToMembers")} style={TabLargeStyle} />
                    </AppBarTabs>
                </AppBar>
            </MuiContainer>
        );
    }

    ///
    private renderFormMembers() {
        return (
            <MuiContainer margin="normal">

                <GroupEditMember
                    groupId={this.state.record.groupId}
                    membersChanged={this.state.record.changedMembers}
                    readonly={this.state.record.groupType != GroupType.UserDefined}
                    onChange={(members: GroupItemChangedDto[]) => this.updateRecord("members", members)}
                    onEndEdit={() => { }}
                />

            </MuiContainer >
        );
    }

    private renderFormPermissions() {
        const { ownRoles, groupsRoles } = this.state.record;
        return (
            <MuiContainer margin="normal">
                <GroupEditRole
                    ownRoles={ownRoles}
                    groupsRoles={groupsRoles}
                    onChange={(v) => this.updateRecord("ownRoles", v)}
                />

            </MuiContainer >
        );
    }

    ///
    private renderFormTopics() {
        return (
            <MuiContainer margin="normal">
                <GroupEditTopic
                    groupId={this.state.record.groupId}
                    topicsChanged={this.state.record.changedSuggestedTopics}
                    onChange={(topics: GroupItemChangedDto[]) => this.updateRecord("topics", topics)}
                    onEndEdit={() => { }}
                />

            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.prepareComponent();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    public render() {
        const { dialog, formCycle } = this.state;

        if (formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport
                error={this.state.lastError}
                action={
                    <ButtonBack
                        text={this.res("BackToList")}
                        onClick={() => this.props.onClose(false)}
                    />
                }
            />;
        }

        if (formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }
        return (
            <Grid container justifyContent="space-around">
                <Grid item xs>

                    <div className={this.props.classes.formContainer}>
                        {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                        <RequestErrorDialog
                            open={this.state.formCycle == RequestLifeCycle.PostFailed}
                            error={this.state.lastError}
                            onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                        />

                        {dialog}
                        {this.renderToolbar()}
                        {this.renderFormMain()}
                        {this.renderFormMenuBar()}
                        {
                            this.state.currentTab == TabOptions.Members
                                ? (
                                    <>
                                        {this.renderFormMembers()}
                                    </>
                                )
                                : (
                                    <>
                                        {this.renderFormTopics()}
                                        {this.renderFormPermissions()}
                                    </>
                                )
                        }
                        {this.renderRecordInfo()}

                        <Prompt
                            when={this.isRecordChanged()}
                            message={this.props.t("Common.Question.Unsave")}
                        />
                    </div>

                </Grid>
            </Grid>
        );
    }
}

export default withTranslation()(withStyles(FormStyleClasses)(withSnackbar(GroupEdit)));